<template>
  <div v-show="!pending" v-style:bg="backgroundColor" :class="{ 'overflow-x-clip': isEdgeToEdge }">
    <cms-recommendation-carousel
      :background-type
      :class="insetClasses"
      :currency="currency[locale]"
      :edge-to-edge="isEdgeToEdge"
      :loading
      :navigation-type="content.navigationType"
      :products="recommendedProducts"
      :quickshop
      :title="content.title"
      :title-class="invertedPaddings"
      :title-color="content.titleColor"
      :title-html="content.titleHtml"
      :title-style="content.titleStyle"
      :variant="content.variant"
      @product-click="handleProductClick"
    />
  </div>
</template>

<script lang="ts" setup>
import type { BloomreachRecommendations } from '#types/components/cms/bloomreach-recommendations'
import type { LayoutVariant } from '#types/components/cms/recommendation-carousel'
import { getPageInfo } from '#commerce/gtm/page'
import { RecommendationStyleContextKey } from '#content/contexts/recommendation'

const { content, edgeToEdge, pageContext } = defineProps<{
  content: BloomreachRecommendations
  pageContext?: boolean
  edgeToEdge?: boolean
}>()

const {
  backgroundColor,
  backgroundType,
  bloomreachIndex,
  cardTextColor: textColor,
  ctaStyle,
  originalPriceColor,
  quickShopVisibility,
  salePriceColor,
  showRatings,
  size,
  textLinkColor,
  textLinkHoverColor,
  textLinkStyle,
  textLinkUnderline,
  title,
  whiteBackground,
} = content

provide(RecommendationStyleContextKey, {
  ctaStyle,
  originalPriceColor,
  salePriceColor,
  textColor,
  textLinkColor,
  textLinkHoverColor,
  textLinkStyle,
  textLinkUnderline,
  whiteBackground
})

const variantOverride = inject('variantOverride', undefined) as LayoutVariant | undefined

const cms = useAppConfig().components.cms
const { enableQuickShop } = cms.productRecommendations
const insetClasses = getContentInset(content, cms.section)
const invertedPaddings = pageContext ? getInvertedPaddings(content, cms.section) : ''
// Outside a page context the section strips componentSidePaddings/componentMaxWidth,
// so there is no viewport-centered container for the bleed geometry to align to
const isEdgeToEdge = computed(() => pageContext && (edgeToEdge || !!cms.bloomreachRecommendations.edgeToEdge))
const locale = useLocale()
const { handleBloomreachProductClick, pending } = useBloomreach()
const { $gtm } = useNuxtApp()
const route = useRoute()
const { currency } = useRuntimeConfig().public

const loading = ref(true)
const recommendedProducts = ref<Awaited<ReturnType<typeof extractBloomReachProduct>[]>>([])

const [placement] = variantOverride === 'search-hub' ? [extractPageType('/searchhub')] : getPageInfo(route, {})
const quickshop = quickShopVisibility ? quickShopVisibility === 'visible' : variantOverride === 'search-hub' ? false : enableQuickShop

const getRecommendedProducts = (products = []) =>
  products.slice(0, size).map((product) => extractBloomReachProduct(product, showRatings))

const handleProductClick = (product, position: number) => {
  handleBloomreachProductClick(product, position, title, variantOverride === 'search-hub' ? '/searchhub' : route.path)
}

const pushImpressions = () => {
  const productItems = recommendedProducts.value.map((product) => ({
    ...product,
    price: +product.price.highOriginal,
    salePrice: +product.price.highCurrent
  }))

  $gtm.queueEvent('product.onProductRecImpressions', productItems, {
    currency: currency[locale],
    placement,
    title
  })
}

watchOnce(() => !loading.value, () => {
  pushImpressions()
})

onActivated(() => {
  if (recommendedProducts.value?.length) {
    pushImpressions()
  }
})

const loadProducts = () => {
  const products = (variantOverride === 'search-hub' ? window.vfrecs?.products : window.vfbr)?.[bloomreachIndex || 0] || []
  recommendedProducts.value = getRecommendedProducts(products)
  loading.value = false
}

onMounted(() => {
  loadProducts()
  window.addEventListener('recalculateBloomreachVfa', loadProducts)
})

onBeforeUnmount(() => {
  window.removeEventListener('recalculateBloomreachVfa', loadProducts)
})
</script>
